import { getNameByField } from './Utils'
import React, { useEffect, useState } from 'react'
import { IconButton, InputAdornment, OutlinedInput, Stack } from '@mui/material'
import { red } from '@mui/material/colors'
import { AddCircle, Cancel, Save } from '@mui/icons-material'
import useResponsive from 'hooks/useResponsive'
import { useTheme } from '@mui/material/styles'
import CircularProgress from '@mui/material/CircularProgress'
import { Setup } from '../../../models/interfaces/setup.interface'
import { WorkoutTypeEnum } from '../../../models/enums/workout-type.enum'
import { useAppDispatch, useAppSelector } from '../../../redux/hooks'
import { modalAddFieldsData } from '../../../redux/reducers/modal-add-fields/modal-add-fields.selectors'
import {
  saveSetup, updateSetupGroupTrainingWorkout
} from '../../../redux/reducers/training-workout/training-workout.actions'
import { showMessage } from '../../../redux/reducers/snackbar/snackbar.actions'
import {
  showModalAddFields
} from '../../../redux/reducers/modal-add-fields/modal-add-fields.actions'
import { selectedTrainingWorkoutType } from '../../../redux/reducers/training-workout/training-workout.selectors'
import { useTranslation } from 'react-i18next'

type Props = {
  setup: Setup[]
  trainingWorkoutGroupId?: string
  onClose: () => void
  isGrouped?: boolean
  workoutType: WorkoutTypeEnum
}

export const SetupFormGroup = ({ setup, trainingWorkoutGroupId, onClose, workoutType = WorkoutTypeEnum.BODYBUILDING }: Props) => {
  const dispatch = useAppDispatch()
  const [isSaving, setIsSaving] = useState(false)
  const [newSetup, setNewSetup] = useState<Setup[]>(setup)
  const trainingWorkoutType = useAppSelector(selectedTrainingWorkoutType)
  const theme = useTheme()
  const { t } = useTranslation()
  const mdUp = useResponsive('up', 'md')
  const data = useAppSelector(modalAddFieldsData)

  useEffect(() => {
    if (data && data.id === trainingWorkoutGroupId) {
      setNewSetup(data.setup)
    }
  }, [data])

  const handleSaveSetup = async () => {
    try {
      setIsSaving(false)
      dispatch(saveSetup({ setups: newSetup, id: String(trainingWorkoutGroupId) }))
      onClose()
      await dispatch(updateSetupGroupTrainingWorkout({
        payload: { setup: newSetup },
        groupTrainingWorkoutId: String(trainingWorkoutGroupId),
        type: trainingWorkoutType
      }))
      handleShowAddModal(false)
      dispatch(showMessage({ message: t('general.message.successDescription') }))
    } catch (e) {
      dispatch(showMessage({ message: t('general.message.errorDescription'), type: 'error' }))
    } finally {
      setIsSaving(false)
    }
  }

  const handleOnChange = (value: any, item: any) => {
    const changed = newSetup?.map((obj: any) => {
      if (obj.field === item.field) return { ...obj, value }
      return obj
    })
    setNewSetup(changed)
  }

  const handleRemoveField = async (field: string) => {
    try {
      setIsSaving(false)
      const filtered = newSetup.filter(item => item.field !== field)
      await dispatch(updateSetupGroupTrainingWorkout({
        payload: { setup: filtered },
        groupTrainingWorkoutId: String(trainingWorkoutGroupId),
        type: trainingWorkoutType
      }))
      dispatch(showMessage({ message: t('general.message.successDescription') }))
      setNewSetup(filtered)
    } catch (e) {
      dispatch(showMessage({ message: t('general.message.errorDescription'), type: 'error' }))
    } finally {
      setIsSaving(false)
    }
  }

  const handleShowAddModal = (visible: boolean) => {
    const payload = { data: { setup: newSetup, id: String(trainingWorkoutGroupId), workoutType }, isVisible: visible }
    dispatch(showModalAddFields(payload))
  }

  return (
    <Stack p={2}>
      <Stack direction={(mdUp ? 'row' : 'column')} alignItems="center">
        {newSetup?.map((item: Setup) => {
          return (
            <Stack key={item.field} mr={2}>
              <span style={{ color: theme.palette.grey[300], marginLeft: 1, marginBottom: 2, fontSize: 14 }}>
                {getNameByField(item.field)}
              </span>
              <OutlinedInput
                style={{ backgroundColor: theme.palette.grey[900], borderColor: theme.palette.grey[900] }}
                size="small"
                placeholder={getNameByField(item.field)}
                value={item.value}
                onChange={e => handleOnChange(e.target.value, item)}
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton
                      title="Excluir campo"
                      arial-label={getNameByField(item.field)}
                      onClick={async () => await handleRemoveField(item.field)}
                      style={{ marginTop: -40, marginRight: -25, zIndex: 1 }}
                    >
                      <Cancel sx={{ color: red[900] }} />
                    </IconButton>
                  </InputAdornment>
                }
              />
            </Stack>
          )
        })}
        <Stack mt={2} direction="row">
          <IconButton color="default" onClick={handleSaveSetup} disabled={isSaving}>
            {isSaving ? <CircularProgress size={18} /> : <Save/>}
          </IconButton>
          <IconButton onClick={() => handleShowAddModal(true)}>
            <AddCircle></AddCircle>
          </IconButton>
        </Stack>
      </Stack>
    </Stack>
  )
}
